import { Edge, Node } from './types';

export interface ConnectionPath {
  nodeIds: string[];
  edges: Edge[];
}

// Connections are traversed in either direction; this describes association,
// not ownership or a claim about the direction of a relationship.
export function findConnectionPath(nodes: Node[], edges: Edge[], source: string, target: string): ConnectionPath | null {
  const nodeIds = new Set(nodes.map(node => String(node.id)));
  if (!nodeIds.has(source) || !nodeIds.has(target)) return null;
  const adjacency = new Map<string, { nodeId: string; edge: Edge }[]>();
  edges.forEach(edge => {
    const from = String(edge.from), to = String(edge.to);
    if (!nodeIds.has(from) || !nodeIds.has(to)) return;
    if (!adjacency.has(from)) adjacency.set(from, []);
    if (!adjacency.has(to)) adjacency.set(to, []);
    adjacency.get(from)!.push({ nodeId: to, edge });
    adjacency.get(to)!.push({ nodeId: from, edge });
  });
  const previous = new Map<string, { nodeId: string; edge: Edge }>();
  const visited = new Set([source]);
  const queue = [source];
  for (let index = 0; index < queue.length; index++) {
    const current = queue[index];
    if (current === target) {
      const path: ConnectionPath = { nodeIds: [target], edges: [] };
      let cursor = target;
      while (cursor !== source) {
        const step = previous.get(cursor)!;
        path.edges.unshift(step.edge);
        path.nodeIds.unshift(step.nodeId);
        cursor = step.nodeId;
      }
      return path;
    }
    for (const next of adjacency.get(current) || []) {
      if (visited.has(next.nodeId)) continue;
      visited.add(next.nodeId);
      previous.set(next.nodeId, { nodeId: current, edge: next.edge });
      queue.push(next.nodeId);
    }
  }
  return null;
}

export function getBranchToHide(nodes: Node[], edges: Edge[], source: string, target: string): Set<string> {
  if (source === target || !nodes.some(node => String(node.id) === target)) return new Set();
  const adjacency = new Map<string, string[]>();
  const ids = new Set(nodes.map(node => String(node.id)));
  if (!ids.has(source)) return new Set();
  edges.forEach(edge => {
    const from = String(edge.from), to = String(edge.to);
    if (!ids.has(from) || !ids.has(to)) return;
    if (!adjacency.has(from)) adjacency.set(from, []);
    if (!adjacency.has(to)) adjacency.set(to, []);
    adjacency.get(from)!.push(to);
    adjacency.get(to)!.push(from);
  });
  const traverse = (exclude?: string) => {
    const reachable = new Set([source]);
    const queue = [source];
    for (let index = 0; index < queue.length; index++) {
      for (const next of adjacency.get(queue[index]) || []) {
        if (next === exclude || reachable.has(next)) continue;
        reachable.add(next);
        queue.push(next);
      }
    }
    return reachable;
  };
  const originallyReachable = traverse();
  const remainingReachable = traverse(target);
  return new Set([target, ...Array.from(originallyReachable).filter(id => !remainingReachable.has(id))]);
}

export function getConnectionNodeName(node: Node): string {
  return String(node.properties?.organization_name || node.properties?.funktionar_namn ||
    node.properties?.street || (node as any).name || node.properties?.organization_number || node.id);
}
